import { api } from "./config";

export async function SendMessage(phone, message) {
  try {
    const response = await api().post("/whatsapp/sendmessage/" + phone, {
      message,
    });

    // Handle successful response
    if (response.status === 200 && response.data.success) {
      return {
        status: 200,
        success: true,
        message: response.data.message,
        data: response.data.data
      };
    } else if (!response.data.success) {
      return {
        status: response.status || 400,
        success: false,
        message: response.data.message || "Failed to send message"
      };
    }
    
    return response.data;
  } catch (error) {
    console.error("SendMessage error:", error);
    
    // Handle error responses
    if (error.response && error.response.data) {
      return {
        status: error.response.status,
        success: false,
        message: error.response.data.message || "Failed to send WhatsApp message",
        data: error.response.data
      };
    }
    
    // Handle network or other errors
    return {
      status: 500,
      success: false,
      message: "Network error or WhatsApp service unavailable"
    };
  }
}

// WhatsApp Multi-Instance API functions
export async function sendMessageToCustomer(customerId, message) {
  try {
    const response = await api().post("/whatsapp/customers/send", {
      customerId,
      message
    });
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("sendMessageToCustomer error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to send WhatsApp message"
    };
  }
}

export async function enqueuePickupNotification(
  customerId,
  message,
  trackingNumbers = [],
  stationId
) {
  try {
    const response = await api().post("/whatsapp/jobs/arrival-notification", {
      customerId,
      message,
      trackingNumbers,
      stationId,
    });

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("enqueuePickupNotification error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to queue pickup notification",
    };
  }
}

export async function enqueueDeliveryNotification(
  customerId,
  message,
  trackingNumbers = []
) {
  try {
    const response = await api().post("/whatsapp/jobs/delivery-notification", {
      customerId,
      message,
      trackingNumbers,
    });

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("enqueueDeliveryNotification error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to queue delivery notification",
    };
  }
}

export async function enqueueAgentNotification(
  agentId,
  station,
  message,
  trackingNumbers = [],
  customerGroupId
) {
  try {
    const response = await api().post("/whatsapp/jobs/agent-notification", {
      agentId,
      station,
      message,
      trackingNumbers,
      customerGroupId,
    });

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("enqueueAgentNotification error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to queue agent notification",
    };
  }
}

export async function getAllWhatsAppStatus() {
  try {
    const response = await api().get("/whatsapp/groups/status");
    
    // The backend returns whatsappStatus as a flat string value
    // No need to extract from nested structure as the backend handles it
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("getAllWhatsAppStatus error:", error);
    
    // If 401, wait and retry once
    if (error.response?.status === 401) {
      console.warn("🔄 Auth issue (401) - waiting 2 seconds and retrying once...");
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      try {
        const retryResponse = await api().get("/whatsapp/groups/status");
        console.log("✅ Status fetch succeeded on retry");
        return {
          success: true,
          data: retryResponse.data
        };
      } catch (retryError) {
        console.error("❌ Status fetch still failed after retry:", retryError);
        return {
          success: false,
          message: retryError.response?.data?.message || "Failed to fetch WhatsApp status (retry failed)"
        };
      }
    }
    
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch WhatsApp status"
    };
  }
}

// `apiBase`: "settings" (default) = the admin WhatsApp settings surface (/api/whatsapp,
// admin-only permission); "mychat" = the per-user "WhatsApp Chat" menu (/api/whatsapp-chat,
// gated by instance assignment instead). Same shape of response either way.
export async function getGroupStatus(groupId, apiBase = "settings") {
  const path =
    apiBase === "mychat" ? `/whatsapp-chat/instances/${groupId}/status` : `/whatsapp/groups/${groupId}/status`;
  try {
    const response = await api().get(path);

    // The backend returns whatsappStatus as a flat string value
    // No need to extract from nested structure as the backend handles it
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("getGroupStatus error:", error);

    // If 401, wait and retry once
    if (error.response?.status === 401) {
      console.warn("🔄 Auth issue (401) - waiting 2 seconds and retrying once...");
      await new Promise(resolve => setTimeout(resolve, 2000));

      try {
        const retryResponse = await api().get(path);
        console.log("✅ Group status fetch succeeded on retry");
        return {
          success: true,
          data: retryResponse.data
        };
      } catch (retryError) {
        console.error("❌ Group status fetch still failed after retry:", retryError);
        return {
          success: false,
          message: retryError.response?.data?.message || "Failed to fetch group status (retry failed)"
        };
      }
    }
    
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch group status"
    };
  }
}

// Ask for a scannable QR for an instance that isn't logged in. The backend returns the live QR
// if there is one, otherwise (re)starts the client — which can take a while. Response data:
// { status: "ready" } (saved session logged straight in) or { status: "connecting", qrCode?, qrRemainingTime? }.
// WhatsApp Settings only — the chat pages don't pair instances.
export async function requestGroupQR(groupId) {
  const path = `/whatsapp/groups/${groupId}/qr`;
  try {
    // Starting headless Chromium + WhatsApp Web can outlast the default 30s timeout.
    const response = await api().post(path, null, { timeout: 90000 });
    return { success: true, data: response.data };
  } catch (error) {
    console.error("requestGroupQR error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to get a new QR code"
    };
  }
}

export async function sendGroupMessage(groupId, phoneNumber, message) {
  try {
    const response = await api().post(`/whatsapp/groups/${groupId}/send`, {
      phoneNumber,
      message
    });
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("sendGroupMessage error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to send group message"
    };
  }
}

// Bulk WhatsApp messaging with attachments
export async function sendBulkWhatsAppMessage(messageData, progressCallback) {
  try {
    // Create FormData for file uploads
    const formData = new FormData();
    
    // Add message data
    formData.append('customers', JSON.stringify(messageData.customers));
    formData.append('message', messageData.message);
    
    // Add attachment files
    messageData.attachments.forEach((attachment, index) => {
      formData.append(`attachments`, attachment.file);
    });

    // Calculate estimated timeout based on customers and attachments
    const baseTimePerCustomer = 5000; // 5 seconds max delay per customer
    const timePerAttachment = 2000; // 2 seconds per attachment
    const totalCustomers = messageData.customers.length;
    const totalAttachments = messageData.attachments.length;
    
    // Estimated time: (customers × base delay) + (total attachments × attachment delay) + buffer
    const estimatedTime = (totalCustomers * baseTimePerCustomer) + 
                         (totalAttachments * timePerAttachment * totalCustomers) + 
                         30000; // 30 second buffer
    
    const minTimeout = 60000; // Minimum 1 minute
    const maxTimeout = 600000; // Maximum 10 minutes
    const calculatedTimeout = Math.min(Math.max(estimatedTime, minTimeout), maxTimeout);
    
    console.log(`📱 Bulk WhatsApp timeout set to: ${Math.round(calculatedTimeout/1000)} seconds`);
    console.log(`📊 Real-time progress will be tracked via Socket.IO`);

    const response = await api().post("/whatsapp/bulk-send", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: calculatedTimeout, // Dynamic timeout based on message count
      onUploadProgress: (progressEvent) => {
        // This handles file upload progress only
        const uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log('📤 Upload progress:', uploadProgress, '%');
      }
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("sendBulkWhatsAppMessage error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to send bulk messages"
    };
  }
}

// Send a Cargo Receipt handoff to one customer as ordered parts in one queue
// (receipt image(s) → claim terms/warning text). The parcel list itself already
// went out earlier as part of the Cargo Prep notification, so it isn't repeated here.
export async function sendCargoReceipt({ customer, packageIds, claimMessage, attachments }) {
  try {
    const formData = new FormData();
    formData.append("customer", JSON.stringify(customer));
    formData.append("packageIds", JSON.stringify(packageIds || []));
    formData.append("claimMessage", claimMessage || "");
    (attachments || []).forEach((attachment) => {
      formData.append("attachments", attachment.file);
    });

    const response = await api().post("/whatsapp/cargo-receipt-send", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    });
    return { success: true, data: response.data };
  } catch (error) {
    console.error("sendCargoReceipt error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to send cargo receipt",
    };
  }
}

// Session Management Functions
export async function clearSessionData(groupId) {
  try {
    const response = await api().post(`/whatsapp/groups/${groupId}/clear-session`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("clearSessionData error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to clear session data"
    };
  }
}

export async function completeReinitialization(groupId) {
  try {
    const response = await api().post(`/whatsapp/groups/${groupId}/complete-reinit`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("completeReinitialization error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to complete reinitialization"
    };
  }
}

export async function recoverAuthentication(groupId) {
  try {
    const response = await api().post(`/whatsapp/groups/${groupId}/recover-auth`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("recoverAuthentication error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to recover authentication"
    };
  }
}

// Queue Management Functions
export async function listQueueJobs({ status, messageType, customerId, groupId, failureReasonCategory, failedOnly, search, fromDate, toDate, limit, skip, showDeleted } = {}) {
  try {
    const params = new URLSearchParams();
    if (status) params.append("status", status);
    if (messageType) params.append("messageType", messageType);
    if (customerId) params.append("customerId", customerId);
    if (groupId) params.append("groupId", groupId);
    if (failureReasonCategory) params.append("failureReasonCategory", failureReasonCategory);
    if (failedOnly) params.append("failedOnly", "true");
    if (search) params.append("search", search);
    if (fromDate) params.append("fromDate", fromDate);
    if (toDate) params.append("toDate", toDate);
    if (limit) params.append("limit", limit);
    if (skip) params.append("skip", skip);
    if (showDeleted) params.append("showDeleted", "true");

    const response = await api().get(`/whatsapp/jobs?${params.toString()}`);
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error("listQueueJobs error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to fetch queue jobs" };
  }
}

// WhatsApp Logs screen — "Instance Logs" tab (disconnects, restarts, escalations, etc.)
export async function listInstanceLogs({ groupId, eventType, severity, fromDate, toDate, limit, skip } = {}) {
  try {
    const params = new URLSearchParams();
    if (groupId) params.append("groupId", groupId);
    if (eventType) params.append("eventType", eventType);
    if (severity) params.append("severity", severity);
    if (fromDate) params.append("fromDate", fromDate);
    if (toDate) params.append("toDate", toDate);
    if (limit) params.append("limit", limit);
    if (skip) params.append("skip", skip);

    const response = await api().get(`/whatsapp/logs/instances?${params.toString()}`);
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error("listInstanceLogs error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to fetch instance logs" };
  }
}

export async function getQueueJob(jobId) {
  try {
    const response = await api().get(`/whatsapp/jobs/${jobId}`);
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error("getQueueJob error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to fetch job" };
  }
}

export async function retryQueueJob(jobId) {
  try {
    const response = await api().post(`/whatsapp/jobs/${jobId}/retry`);
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error("retryQueueJob error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to retry job" };
  }
}

export async function restoreQueueJob(jobId) {
  try {
    const response = await api().post(`/whatsapp/jobs/${jobId}/restore`);
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error("restoreQueueJob error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to restore job" };
  }
}

export async function cancelQueueJob(jobId) {
  try {
    const response = await api().post(`/whatsapp/jobs/${jobId}/cancel`);
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error("cancelQueueJob error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to cancel job" };
  }
}

export async function deleteQueueJob(jobId) {
  try {
    const response = await api().delete(`/whatsapp/jobs/${jobId}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error("deleteQueueJob error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to delete job" };
  }
}

export async function bulkRetryJobs(jobIds) {
  try {
    const response = await api().post(`/whatsapp/jobs/bulk/retry`, { jobIds });
    return { success: true, data: response.data };
  } catch (error) {
    console.error("bulkRetryJobs error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to bulk retry" };
  }
}

export async function bulkCancelJobs(jobIds) {
  try {
    const response = await api().post(`/whatsapp/jobs/bulk/cancel`, { jobIds });
    return { success: true, data: response.data };
  } catch (error) {
    console.error("bulkCancelJobs error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to bulk cancel" };
  }
}

export async function bulkDeleteJobs(jobIds) {
  try {
    const response = await api().post(`/whatsapp/jobs/bulk/delete`, { jobIds });
    return { success: true, data: response.data };
  } catch (error) {
    console.error("bulkDeleteJobs error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to bulk delete" };
  }
}

export async function forceReinitialize(groupId) {
  try {
    const response = await api().post(`/whatsapp/groups/${groupId}/reinitialize`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("forceReinitialize error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to force reinitialize"
    };
  }
}

// WhatsApp Web-style inbox (chat list + message thread) — shared by two entry points:
//   "settings" (default): WhatsApp settings > instance > Open Chat, admin-only /api/whatsapp
//   "mychat": the per-user "WhatsApp Chat" menu, /api/whatsapp-chat (instance-assignment gated)
// Every function below takes the same trailing `apiBase` param for this; see getGroupStatus
// above for the same convention.
function inboxPrefix(groupId, apiBase) {
  return apiBase === "mychat" ? `/whatsapp-chat/instances/${groupId}` : `/whatsapp/groups/${groupId}/inbox`;
}

export async function getInboxChats(groupId, { limit = 50, offset = 0, q = "", filter = "all", apiBase = "settings" } = {}) {
  try {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (q) params.set("q", q);
    if (filter && filter !== "all") params.set("filter", filter);
    const response = await api().get(`${inboxPrefix(groupId, apiBase)}/chats?${params.toString()}`);
    return { success: true, data: response.data.data, hasMore: !!response.data.hasMore, total: response.data.total };
  } catch (error) {
    console.error("getInboxChats error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to fetch chats" };
  }
}

export async function getInboxChatMessages(groupId, chatId, { before = null, limit = 50, apiBase = "settings" } = {}) {
  try {
    const params = new URLSearchParams({ limit: String(limit) });
    if (before) params.set("before", before);
    const response = await api().get(
      `${inboxPrefix(groupId, apiBase)}/chats/${encodeURIComponent(chatId)}/messages?${params.toString()}`,
      { timeout: 60000 } // loading older history can take a few WhatsApp round-trips
    );
    return { success: true, data: response.data.data, hasMore: !!response.data.hasMore };
  } catch (error) {
    console.error("getInboxChatMessages error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to fetch messages" };
  }
}

// `quotedMessageId`: send as a reply to that message.
export async function sendInboxChatMessage(groupId, chatId, text, apiBase = "settings", { quotedMessageId = null } = {}) {
  try {
    const response = await api().post(
      `${inboxPrefix(groupId, apiBase)}/chats/${encodeURIComponent(chatId)}/messages`,
      quotedMessageId ? { text, quotedMessageId } : { text }
    );
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error("sendInboxChatMessage error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to send message" };
  }
}

// Edit one of our own text messages (WhatsApp allows ~15 minutes after sending).
export async function editInboxChatMessage(groupId, chatId, messageId, text, apiBase = "settings") {
  try {
    const response = await api().post(
      `${inboxPrefix(groupId, apiBase)}/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}/edit`,
      { text }
    );
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error("editInboxChatMessage error:", error);
    return { success: false, code: error.response?.data?.code, message: error.response?.data?.message || "Failed to edit message" };
  }
}

// "Delete for everyone" one of our own messages (WhatsApp allows ~2 days after sending).
export async function revokeInboxChatMessage(groupId, chatId, messageId, apiBase = "settings") {
  try {
    const response = await api().post(
      `${inboxPrefix(groupId, apiBase)}/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}/revoke`
    );
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error("revokeInboxChatMessage error:", error);
    return { success: false, code: error.response?.data?.code, message: error.response?.data?.message || "Failed to delete message" };
  }
}

// `asVoice`: a voice message recorded in the chat — the backend sends it as a WhatsApp voice note.
// `quotedMessageId`: send as a reply to that message.
export async function sendInboxChatMedia(groupId, chatId, file, apiBase = "settings", { asVoice = false, quotedMessageId = null } = {}) {
  try {
    const formData = new FormData();
    formData.append("media", file);
    if (asVoice) formData.append("asVoice", "true");
    if (quotedMessageId) formData.append("quotedMessageId", quotedMessageId);
    const response = await api().post(
      `${inboxPrefix(groupId, apiBase)}/chats/${encodeURIComponent(chatId)}/media`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error("sendInboxChatMedia error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to send media" };
  }
}

// Several photos at once, each with its own caption (same order). The first photo is the reply
// when `quotedMessageId` is set. On failure `sent` = how many already went out (retry the rest).
export async function sendInboxChatMediaBatch(groupId, chatId, files, captions, apiBase = "settings", { quotedMessageId = null } = {}) {
  try {
    const formData = new FormData();
    files.forEach((f) => formData.append("media", f));
    formData.append("captions", JSON.stringify(captions || []));
    if (quotedMessageId) formData.append("quotedMessageId", quotedMessageId);
    const response = await api().post(
      `${inboxPrefix(groupId, apiBase)}/chats/${encodeURIComponent(chatId)}/media/batch`,
      formData,
      // ~1s per photo on WhatsApp's side plus the upload itself.
      { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 + files.length * 15000 }
    );
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error("sendInboxChatMediaBatch error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to send photos",
      sent: error.response?.data?.data?.sent || 0,
    };
  }
}

// Not an axios call — used directly as an <img>/<a> src so the browser fetches it (and sends
// the httpOnly auth cookie automatically, same-origin). Build off the same base URL config.js
// uses so this stays correct if REACT_APP_BASE_URL ever changes.
export function getInboxChatMediaUrl(groupId, chatId, messageId, apiBase = "settings") {
  const base = process.env.REACT_APP_BASE_URL || "/api";
  return `${base}${inboxPrefix(groupId, apiBase)}/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}/media`;
}

// Returns a small JPEG data URI (or null) from the backend's stored copy — listing chats never
// asks WhatsApp. `refresh` (when a chat is opened) re-checks that one chat with WhatsApp.
export async function getInboxChatProfilePic(groupId, chatId, apiBase = "settings", { refresh = false } = {}) {
  try {
    const response = await api().get(
      `${inboxPrefix(groupId, apiBase)}/chats/${encodeURIComponent(chatId)}/profile-pic${refresh ? "?refresh=1" : ""}`
    );
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to fetch profile photo" };
  }
}

export async function getInboxChatInfo(groupId, chatId, apiBase = "settings") {
  try {
    const response = await api().get(
      `${inboxPrefix(groupId, apiBase)}/chats/${encodeURIComponent(chatId)}/info`
    );
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error("getInboxChatInfo error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to fetch contact info" };
  }
}

// "Media, links and docs": kind = media | docs | links; `before` = nextBefore of the previous page.
// Returns { success, data: { items, hasMore, nextBefore } } — items newest first.
export async function getInboxChatMediaItems(groupId, chatId, kind, { before = null, apiBase = "settings" } = {}) {
  try {
    const response = await api().get(`${inboxPrefix(groupId, apiBase)}/chats/${encodeURIComponent(chatId)}/media-items`, {
      params: { kind, ...(before ? { before } : {}) },
      timeout: 90000,
    });
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error("getInboxChatMediaItems error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to load media" };
  }
}

export async function markInboxChatUnread(groupId, chatId, apiBase = "settings") {
  try {
    await api().post(`${inboxPrefix(groupId, apiBase)}/chats/${encodeURIComponent(chatId)}/unread`);
    return { success: true };
  } catch (error) {
    console.error("markInboxChatUnread error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to mark chat unread" };
  }
}

// Pin / unpin a chat (WhatsApp allows up to 3 pinned chats).
export async function setInboxChatPinned(groupId, chatId, pinned, apiBase = "settings") {
  try {
    const response = await api().post(`${inboxPrefix(groupId, apiBase)}/chats/${encodeURIComponent(chatId)}/pin`, { pinned });
    return { success: true, pinned: !!response.data?.data?.pinned };
  } catch (error) {
    console.error("setInboxChatPinned error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to pin chat" };
  }
}

// Forward messages (ids as returned by getInboxChatMessages, chronological) to up to 5 chats.
export async function forwardInboxMessages(groupId, messageIds, toChatIds, apiBase = "settings") {
  try {
    const response = await api().post(`${inboxPrefix(groupId, apiBase)}/forward`, { messageIds, toChatIds }, { timeout: 120000 });
    const results = response.data.data || [];
    return { success: !!response.data.success, okCount: results.filter((r) => r.ok).length, message: response.data.message };
  } catch (error) {
    console.error("forwardInboxMessages error:", error);
    return { success: false, okCount: 0, message: error.response?.data?.message || "Failed to forward" };
  }
}

export async function markInboxChatRead(groupId, chatId, apiBase = "settings") {
  try {
    const response = await api().post(
      `${inboxPrefix(groupId, apiBase)}/chats/${encodeURIComponent(chatId)}/read`
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error("markInboxChatRead error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to mark chat read" };
  }
}

// Instances assigned to the current user, for the "WhatsApp Chat" menu's own instance picker.
export async function getMyWhatsappInstances() {
  try {
    const response = await api().get("/whatsapp-chat/instances");
    return { success: true, data: response.data.data || [] };
  } catch (error) {
    console.error("getMyWhatsappInstances error:", error);
    return { success: false, message: error.response?.data?.message || "Failed to fetch WhatsApp instances" };
  }
}

// ---- Quick replies (backend/controllers/whatsappQuickReply.js) ----
// In a chat: list (global + this instance) with `context` for the {variables}/language, and
// manage this instance's replies. `apiBase` as above. Admin "Quick Replies" tab: the
// manage* functions below without a groupId use /whatsapp/quick-replies (global or any group).
function quickReplyPrefix(groupId, apiBase) {
  if (!groupId) return "/whatsapp/quick-replies";
  return `${inboxPrefix(groupId, apiBase)}/quick-replies`;
}

// `data` = { shortcut, title, text: {en,th,lo}, sortOrder, groupId? (admin tab only, "" = global),
// keepAttachmentIds? (update) }, `files` = new photos/videos to add.
function quickReplyFormData(data, files) {
  const formData = new FormData();
  formData.append("shortcut", data.shortcut || "");
  formData.append("title", data.title || "");
  formData.append("text", JSON.stringify(data.text || {}));
  formData.append("sortOrder", String(data.sortOrder || 0));
  if (data.groupId !== undefined) formData.append("groupId", data.groupId || "");
  if (data.keepAttachmentIds) formData.append("keepAttachmentIds", JSON.stringify(data.keepAttachmentIds));
  (files || []).forEach((f) => formData.append("media", f));
  return formData;
}

export async function getQuickReplies(groupId, { chatId = null, apiBase = "settings" } = {}) {
  try {
    const response = await api().get(quickReplyPrefix(groupId, apiBase), { params: chatId ? { chatId } : {} });
    return { success: true, data: response.data.data, context: response.data.context || null };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to fetch quick replies" };
  }
}

// Admin tab list: scope "" = all, "global", or a groupId.
export async function listAllQuickReplies(scope = "") {
  try {
    const response = await api().get("/whatsapp/quick-replies", { params: scope ? { groupId: scope } : {} });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to fetch quick replies" };
  }
}

export async function saveQuickReply(groupId, id, data, files, apiBase = "settings") {
  try {
    const url = id ? `${quickReplyPrefix(groupId, apiBase)}/${id}` : quickReplyPrefix(groupId, apiBase);
    const response = await api().post(url, quickReplyFormData(data, files), {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60000 + (files || []).length * 30000,
    });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to save quick reply" };
  }
}

export async function deleteQuickReply(groupId, id, apiBase = "settings") {
  try {
    await api().post(`${quickReplyPrefix(groupId, apiBase)}/${id}/delete`);
    return { success: true };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to delete quick reply" };
  }
}

// Send a quick reply that has photos/videos (from the stored files); `text` = the filled-in text.
export async function sendQuickReply(groupId, chatId, id, text, apiBase = "settings", { quotedMessageId = null, count = 1 } = {}) {
  try {
    const response = await api().post(
      `${inboxPrefix(groupId, apiBase)}/chats/${encodeURIComponent(chatId)}/quick-replies/${id}/send`,
      quotedMessageId ? { text, quotedMessageId } : { text },
      { timeout: 60000 + count * 60000 }
    );
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, message: error.response?.data?.message || "Failed to send quick reply", sent: error.response?.data?.data?.sent || 0 };
  }
}
